import React from 'react';
import PropTypes from 'prop-types';
import { NOTIFICATION_TYPES } from '../../constants';
import './NotificationPanel.css';

/**
 * Notification dropdown panel.
 *
 * Displays a scrollable list of notifications when the bell icon is clicked.
 * Shows an empty state when there are no notifications.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the panel is visible
 * @param {function} props.onClose - Handler to close the panel
 * @param {Array} props.notifications - Array of notification objects
 * @returns {JSX.Element|null}
 */
function NotificationPanel({ isOpen, onClose, notifications }) {
  if (!isOpen) return null;

  return (
    <div className="notification-panel" role="dialog" aria-label="Notifications">
      {/* Header */}
      <div className="notification-panel__header">
        <button
          type="button"
          className="notification-panel__back"
          onClick={onClose}
          aria-label="Close notifications"
        >
          <span aria-hidden="true">&lsaquo;</span>
        </button>
        <h3 className="notification-panel__title">Notifications</h3>
        <button
          type="button"
          className="notification-panel__settings"
          aria-label="Notification settings"
        >
          <span aria-hidden="true">&#9881;</span>
        </button>
      </div>

      {/* Content */}
      <div className="notification-panel__content">
        {notifications.length === 0 ? (
          <div className="notification-panel__empty">
            <span className="notification-panel__empty-icon" aria-hidden="true">&#128276;</span>
            <p className="notification-panel__empty-title">No notifications at this time</p>
            <span className="notification-panel__empty-desc">
              When you receive a notification, it will show up here.
            </span>
          </div>
        ) : (
          <div className="notification-panel__list">
            {notifications.map((notif) => (
              <NotificationItem key={notif.id} notification={notif} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Individual notification item.
 */
function NotificationItem({ notification }) {
  const { type, user, amount, timestamp, read } = notification;

  const renderText = () => {
    switch (type) {
      case NOTIFICATION_TYPES.TOKEN_MESSAGE:
        return (
          <>
            <strong>{user.username}</strong> sent a{' '}
            <strong>{amount?.toLocaleString()} Token</strong> message
          </>
        );
      case NOTIFICATION_TYPES.WENT_LIVE:
        return (
          <>
            <strong>{user.username}</strong> went{' '}
            <span className="notification-item__highlight">Live!</span>
          </>
        );
      case NOTIFICATION_TYPES.FOLLOW:
        return (
          <>
            <strong>{user.username}</strong> followed you
          </>
        );
      case NOTIFICATION_TYPES.MENTION:
        return (
          <>
            <strong>{user.username}</strong> mentioned you
          </>
        );
      case NOTIFICATION_TYPES.DIRECT_MESSAGE:
        return (
          <>
            <strong>{user.username}</strong> sent you a message
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`notification-item ${!read ? 'notification-item--unread' : ''}`}>
      <div className="notification-item__avatar">
        {user.avatar ? (
          <img src={user.avatar} alt="" />
        ) : (
          <span className="notification-item__avatar-placeholder">
            {user.username.charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      <div className="notification-item__content">
        <p className="notification-item__text">{renderText()}</p>
        <span className="notification-item__time">{timestamp}</span>
      </div>
      {!read && <span className="notification-item__dot" />}
    </div>
  );
}

NotificationPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  notifications: PropTypes.array.isRequired,
};

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired,
};

export default NotificationPanel;
